import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import { AUTH_SESSION } from './actionCreators';
import { IceIcon, PageContainer, PageHeader, TextInput, UserMenu } from '../components';
import { useJspVariables, useNavigation, useAuth, useLoader, useDebugRender } from '../hooks';
import { getLabelFromJspVariables } from '../common/JspUtil';

/*
 Flow:
  Step 0: Show current email + Continue / Cancel
  Step 1: Show OTP input for current email + Continue / Cancel
  Step 2: Show New Email input + Continue / Cancel
  Step 3: Show OTP input for new email + Continue / Cancel
  Step 4: Show Change Email / Cancel final buttons
*/

const PageChangeGlobalEmail = () => {
    useDebugRender('PageChangeGlobalEmail');
    const dispatch = useDispatch();
    const { panelStyle, launcher } = useJspVariables();
    const { navigate } = useNavigation();
    const { showLoader, hideLoader } = useLoader();

    const { authenticatedUser: authenticateUser, isAuthenticated } = useAuth();
    const currentUserEmail = authenticateUser?.displayUser || authenticateUser?.user || '';

    const [step, setStep] = useState(0);
    const [currentOtp, setCurrentOtp] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newEmailOtp, setNewEmailOtp] = useState('');
    const [emailChanged, setEmailChanged] = useState(false);
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(constants.ROUTE_PAGE_LOGIN);
        }
    }, []);

    const handleError = (resp) => {
        if (Number(resp.code) === errorCodes.ERR_EMAIL_VERIFICATION_INVALID_SESSION) {
            dispatch({ type: actions.SESSION_EXPIRED_DIALOG, data: true });
            return;
        }
        setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
    };

    const cancel = () => {
        navigate(constants.ROUTE_PAGE_AUTHENTICATED);
    };

    const continueFromStep0 = async () => {
        setAlert(null);
        showLoader('Sending verification code...');
        try {
            await actions.sendOtpCodeToEmail(currentUserEmail, "GLOBAL", false, currentUserEmail);
            setAlert({ message: getLabelFromJspVariables('alert_verification_code_sent'), severity: 'success' });
            setStep(1);
        } catch (resp) {
            handleError(resp);
        } finally {
            hideLoader();
        }
    };

    const continueFromStep1 = async () => {
        if (!currentOtp) {
            setErrors({ currentOtp: 'Please enter OTP code' });
            return;
        }
        setErrors({});
        setAlert(null);
        showLoader('Verifying current email...');
        try {
            await actions.verifyEmailConfirmationCode(currentUserEmail, currentOtp, currentUserEmail);
            setAlert({ message: getLabelFromJspVariables('alert_email_verified'), severity: 'success' });
            setStep(2);
        } catch (resp) {
            handleError(resp);
        } finally {
            hideLoader();
        }
    };

    const continueFromStep2 = async () => {
        if (!newEmail) {
            setErrors({ newEmail: 'Please enter new email' });
            return;
        }
        const emailRegex = /.+@.+\..+/;
        if (!emailRegex.test(newEmail)) {
            setErrors({ newEmail: 'Invalid email format' });
            return;
        }
        setErrors({});
        setAlert(null);
        showLoader('Sending verification code to new email...');
        try {
            await actions.sendOtpCodeToEmail(newEmail, "GLOBAL", true, currentUserEmail);
            setAlert({ message: getLabelFromJspVariables('alert_verification_code_sent'), severity: 'success' });
            setStep(3);
        } catch (resp) {
            handleError(resp);
        } finally {
            hideLoader();
        }
    };

    const continueFromStep3 = async () => {
        if (!newEmailOtp) {
            setErrors({ newEmailOtp: 'Please enter OTP code for new email' });
            return;
        }
        setErrors({});
        setAlert(null);
        showLoader('Verifying new email...');
        try {
            await actions.verifyEmailConfirmationCode(newEmail, newEmailOtp, currentUserEmail);
            setAlert({ message: getLabelFromJspVariables('alert_email_verified'), severity: 'success' });
            setStep(4);
        } catch (resp) {
            handleError(resp);
        } finally {
            hideLoader();
        }
    };

    const changeEmailFinal = async () => {
        setAlert(null);
        showLoader('Changing email...');
        try {
            await actions.changeGlobalIdEmailValidateDomain(newEmail, currentUserEmail);
            // Update Redux auth state with new email
            const current = authenticateUser || {};
            dispatch({ type: AUTH_SESSION, data: { authenticatedUser: { ...current, displayUser: newEmail, user: newEmail, authenticatedUser: newEmail } } });
            setEmailChanged(true);
            setAlert({ message: getLabelFromJspVariables('alert_email_changed'), severity: 'success' });
        } catch (resp) {
            handleError(resp);
        } finally {
            hideLoader();
        }
    };

    const renderStepContent = () => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="subtitle2">{getLabelFromJspVariables('label_current_global_email')}</Typography>
                        <Typography variant="h6" gutterBottom>{currentUserEmail}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={cancel} data-automation-id="change_global_email_step0_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                            <Button variant="contained" onClick={continueFromStep0} data-automation-id="change_global_email_step0_continue_btn">{getLabelFromJspVariables('action_continue')}</Button>
                        </Box>
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <TextInput key="currentOtp" id="currentOtp" label={getLabelFromJspVariables('label_enter_otp_current_email')} value={currentOtp} required
                            autoComplete="one-time-code" numericOnly maxLength={6}
                            error={!!errors.currentOtp} helperText={errors.currentOtp}
                            onChange={e => { setCurrentOtp(e.target.value); setErrors({}); }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={cancel} data-automation-id="change_global_email_step1_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                            <Button variant="contained" onClick={continueFromStep1} data-automation-id="change_global_email_step1_continue_btn">{getLabelFromJspVariables('action_continue')}</Button>
                        </Box>
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <TextInput key="newEmail" id="newEmail" label={getLabelFromJspVariables('label_enter_new_email')} value={newEmail} required
                            error={!!errors.newEmail} helperText={errors.newEmail}
                            onChange={e => { setNewEmail(e.target.value); setErrors({}); }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={cancel} data-automation-id="change_global_email_step2_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                            <Button variant="contained" onClick={continueFromStep2} data-automation-id="change_global_email_step2_continue_btn">{getLabelFromJspVariables('action_continue')}</Button>
                        </Box>
                    </Box>
                );
            case 3:
                return (
                    <Box>
                        <TextInput key="newEmailOtp" id="newEmailOtp" label={`Enter OTP sent to new email (${newEmail})`} value={newEmailOtp} required
                            autoComplete="one-time-code" numericOnly maxLength={6}
                            error={!!errors.newEmailOtp} helperText={errors.newEmailOtp}
                            onChange={e => { setNewEmailOtp(e.target.value); setErrors({}); }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={cancel} data-automation-id="change_global_email_step3_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                            <Button variant="contained" onClick={continueFromStep3} data-automation-id="change_global_email_step3_continue_btn">{getLabelFromJspVariables('action_continue')}</Button>
                        </Box>
                    </Box>
                );
            case 4:
                return (
                    <Box>
                        <Typography variant="body1" gutterBottom>
                            Ready to change email from <strong>{currentUserEmail}</strong> to <strong>{newEmail}</strong>.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={cancel} disabled={emailChanged} data-automation-id="change_global_email_final_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                            <Button variant="contained" onClick={changeEmailFinal} disabled={emailChanged} data-automation-id="change_global_email_final_change_btn">{getLabelFromJspVariables('action_change_email')}</Button>
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    const stepLabels = ['Verify Current Email', 'Enter OTP', 'New Email', 'Verify New Email', 'Confirm Change'];

    return (
        <PageContainer id='pageChangeGlobalEmail' dataAutomationId='page_change_global_email'>
            <Box sx={panelStyle}>
                <UserMenu
                    authenticatedUser={authenticateUser}
                    onLogout={() => dispatch(actions.logoutUserToken(authenticateUser.user))}
                    onNavigate={(route) => navigate(route)}
                />
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="change_global_email_header">{getLabelFromJspVariables('title_change_global_email')}</PageHeader>
                {alert && <Alert id="changeGlobalEmailAlert" data-automation-id="change_global_email_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                    {stepLabels.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent()}
            </Box>
        </PageContainer>
    );
};

export default PageChangeGlobalEmail;
